/**
 * Client-side sorting for `CustomTable`.
 *
 * This replaces `@/utils/getComparator` + `@/utils/stableSort` for the new
 * table. Those stay where they are for `CustomTableLegacy`; they are not worth
 * changing under a component nothing renders any more.
 *
 * The old pair had three defects, and between them they are why so many columns
 * in this repo carry `noSort: true`:
 *
 * 1. `getComparator(order, orderBy)` read `row[orderBy]`, where `orderBy` is the
 *    HEADER ID. Sorting therefore only worked where a column id happened to
 *    equal a top-level field name. Every derived, renamed, nested or
 *    fetched-per-row value read `undefined` for every row, the comparator
 *    returned 0 for every pair, and `stableSort` handed the array back
 *    untouched — a silent no-op under a live-looking sort arrow. `sortKey`
 *    below is the fix: it names where the value actually lives.
 * 2. Values were compared with bare `<` / `>`, so numeric strings sorted
 *    lexicographically ("100" before "9"), formatted values sorted as text, and
 *    null/undefined sorted wherever they happened to fall.
 * 3. The dotted-path branch walked segments with no guard, so a row missing an
 *    intermediate object threw a TypeError part-way through the sort.
 */

/** What a column can be sorted by, once resolved off a row. */
export type SortValue = string | number | boolean | Date | null | undefined;

/**
 * Where a column's sortable value lives, when that isn't `row[header.id]`.
 *
 * A dotted string walks nested objects (`"Advertiser.name"`); a function covers
 * everything else — a value computed from several fields, a lookup through a
 * mapping, a ratio the cell also computes for display.
 *
 * The function form must return the value to ORDER BY, not the string the cell
 * renders. Sort `row.createdAt`, not `"08/05/2026 - 14:22:01 PM"`; sort the
 * ratio, not `"45.2%"`. Formatting is generally not order-preserving, and the
 * date/number detection below deliberately doesn't try to reverse it.
 */
export type SortKey = string | ((row: any) => SortValue);

/**
 * The part of a header this module needs. Declared here rather than imported
 * from `table.interface`, which imports `SortKey` back off this file —
 * `sorting` is the lower-level half of the pair and stays free of the cycle.
 */
export interface SortableHeader {
  id: string;
  sortKey?: SortKey;
}

/** How a column's values are being compared. Decided once per sort, not per pair. */
type SortMode = "number" | "date" | "string";

/** Values that carry no order and always sort to the bottom. See `compare`. */
const isEmpty = (value: SortValue) =>
  value === null || value === undefined || value === "";

/**
 * Numbers dressed up as strings: thousands separators, a leading currency
 * symbol, a trailing percent. These reach the table from the API as often as
 * bare numbers do, and lexicographic order on them is always wrong.
 *
 * Deliberately strict — the whole string has to be a number once the decoration
 * is removed. Anything with a unit or a word in it stays a string, where
 * `localeCompare`'s numeric collation still orders embedded digits sensibly.
 *
 * The alternation matters. Grouped and ungrouped are separate branches because
 * `\d{1,3}(,\d{3})*` alone matches a grouped number but NOT a plain one of four
 * digits or more: "1,234" passed and "1234" did not. `detectMode` needs every
 * value to agree, so one unseparated thousand dropped the whole column to
 * string comparison — ["999", "1000", "1,001"] then sorted as 1,001 / 999 /
 * 1000, because the collator weighed the digit run before the comma against 999.
 * Requiring `+` on the grouped branch keeps "1,23" rejected by both.
 */
const NUMERIC_TEXT = /^[-+]?[$£€]?\s*(\d{1,3}(,\d{3})+|\d+)(\.\d+)?\s*%?$/;

/**
 * ISO-8601-ish only. A looser test (handing everything to `dayjs`) reads far
 * too much as a date — "9" parses, and so does anything with a stray slash — and
 * a column silently sorted as dates is harder to spot than one sorted as text.
 *
 * Display-formatted dates are intentionally NOT matched: a `sortKey` is supposed
 * to point at the raw field, and matching "MM/DD/YYYY" here would only reward
 * pointing it at the rendered string.
 */
const ISO_DATE = /^\d{4}-\d{2}-\d{2}([T\s]|$)/;

const asNumber = (value: SortValue): number | null => {
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  if (typeof value === "boolean") return value ? 1 : 0;
  if (typeof value === "string" && NUMERIC_TEXT.test(value.trim())) {
    const parsed = Number(value.replace(/[^0-9.eE+-]/g, ""));
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
};

const asTime = (value: SortValue): number | null => {
  if (value instanceof Date) {
    const time = value.getTime();
    return Number.isNaN(time) ? null : time;
  }
  if (typeof value === "string" && ISO_DATE.test(value.trim())) {
    const time = Date.parse(value);
    return Number.isNaN(time) ? null : time;
  }
  return null;
};

/**
 * Resolves the value a column orders by, falling back to `row[header.id]` so a
 * column whose id already names its field needs no `sortKey` at all.
 *
 * The path walk stops at the first missing link rather than throwing, which the
 * old one did: a row whose `Advertiser` is null under an `"Advertiser.name"`
 * key took the whole sort down with a TypeError.
 */
export function resolveSortValue(
  row: any,
  header: SortableHeader | undefined
): SortValue {
  if (!row || !header) return undefined;
  const key = header.sortKey ?? header.id;
  if (typeof key === "function") {
    try {
      return key(row);
    } catch {
      // A sortKey is caller-supplied and runs against every row on the page. One
      // malformed record should cost that record its position, not the sort.
      return undefined;
    }
  }
  let current: any = row;
  for (const segment of key.split(".")) {
    if (current === null || current === undefined) return undefined;
    current = current[segment];
  }
  return current;
}

/**
 * Picks one comparison mode for the whole column.
 *
 * Deciding per pair instead looks tempting and breaks transitivity: with
 * ["10", "9", "abc"], a per-pair rule compares the first two as numbers and the
 * rest as text, and `Array.prototype.sort` is free to produce a different order
 * depending on which pairs it happens to visit. One mode for the column is both
 * stable and cheaper.
 *
 * Every non-empty value has to agree — one unparseable entry drops the column to
 * string comparison, which is the mode that always works.
 */
function detectMode(values: Array<SortValue>): SortMode {
  let sawValue = false;
  let numeric = true;
  let dates = true;
  for (const value of values) {
    if (isEmpty(value)) continue;
    sawValue = true;
    if (numeric && asNumber(value) === null) numeric = false;
    if (dates && asTime(value) === null) dates = false;
    if (!numeric && !dates) break;
  }
  if (!sawValue) return "string";
  if (numeric) return "number";
  if (dates) return "date";
  return "string";
}

/**
 * Compares two resolved values under a fixed mode, ascending.
 *
 * Empty always sorts last, in BOTH directions — it is not "smaller", it is
 * absent, and flipping to descending should not fill the top of the table with
 * blank cells. This is deliberately not symmetric with the rest of the ordering.
 */
function compare(a: SortValue, b: SortValue, mode: SortMode): number {
  const aEmpty = isEmpty(a);
  const bEmpty = isEmpty(b);
  if (aEmpty || bEmpty) return aEmpty && bEmpty ? 0 : aEmpty ? 1 : -1;

  if (mode === "number") {
    return (asNumber(a) ?? 0) - (asNumber(b) ?? 0);
  }
  if (mode === "date") {
    return (asTime(a) ?? 0) - (asTime(b) ?? 0);
  }
  // `numeric` so "Item 2" precedes "Item 10", `sensitivity: "base"` so case and
  // accents don't split what reads as one group.
  return String(a).localeCompare(String(b), undefined, {
    numeric: true,
    sensitivity: "base",
  });
}

/**
 * Sorts a page of rows by one header, preserving the original order of ties.
 *
 * Returns the input array unchanged (same reference) when there is nothing to
 * sort by, so a table with no active sort does no work and its rows keep the
 * order the API returned them in.
 *
 * `noSort` is NOT consulted here. It governs whether the header offers a sort
 * control, not whether the table may sort: a list can seed `sortByIndex` /
 * `sortBy` on a column it doesn't want users re-sorting, and
 * `AudienceManager/List` does exactly that with Created At.
 *
 * SCOPE: this sorts the rows it is given. On a server-paginated list that is the
 * current page, not the result set — see `onSortChange` on `CustomTable` for the
 * whole-set path.
 */
export function sortRows<T>(
  rows: Array<T>,
  header: SortableHeader | undefined,
  order: "asc" | "desc"
): Array<T> {
  if (!rows?.length || !header?.id) return rows ?? [];

  const values = rows.map((row) => resolveSortValue(row, header));
  const mode = detectMode(values);
  const direction = order === "desc" ? -1 : 1;

  return rows
    .map((row, index) => ({ row, index, value: values[index] }))
    .sort((a, b) => {
      const result = compare(a.value, b.value, mode);
      // Ties keep their incoming order, and so do the empties the direction
      // flip doesn't apply to — both would otherwise shuffle on every re-render.
      if (result !== 0) {
        return isEmpty(a.value) || isEmpty(b.value) ? result : result * direction;
      }
      return a.index - b.index;
    })
    .map((entry) => entry.row);
}
